<template>
  <div>
    <og-table class="ogtable-opendata">
      <og-table-head v-if="companies.length > 0">
        <tr class="text-xs-left">
          <th>{{ $t('CIF/NIF') }}</th>
          <th>{{ $t('Empresa') }}</th>
          <th>{{ $t('Estado') }}</th>
          <th>{{ $t('Válido hasta') }}</th>
          <th></th>
        </tr>
      </og-table-head>

      <og-table-head v-else>
        <tr>
          <th class="subheading text-xs-left">
            <v-icon>mdi-information-outline</v-icon>
            {{ $t('Todavía no ha iniciado ningún cuestionario en OpenData') }}
          </th>
          <th class="text-xs-right">
            <v-btn v-if="withActionButton" color="primary" :to="'/opendata/form/new/1'">
              {{ $t('Iniciar cuestionario') }}
              <v-icon>mdi-chevron-right</v-icon>
            </v-btn>
          </th>
        </tr>
      </og-table-head>

      <og-table-body v-if="companies.length > 0">
        <tr class="opendata-company-row" v-for="company in companies" :key="company.id">
          <td>
            <v-icon
              color="primary"
              v-if="
                company.status === 'validated' &&
                  !!company.media[0] &&
                  checklistCompletedPercent(company)['completed'] ===
                    checklistCompletedPercent(company)['total']
              "
            >
              mdi-shield-check-outline
            </v-icon>
            <v-icon v-else color="error">mdi-shield-remove-outline</v-icon>
            {{ company.cif }}
          </td>
          <td>{{ company.name }}</td>
          <td>
            <template v-if="company.status === 'validated'">
              <v-icon color="primary">mdi-check-outline</v-icon>
            </template>
            <template v-else-if="company.status === 'draft'">
              <v-icon color="grey">mdi-file-hidden</v-icon>
              <span class="ml-1 grey--text">{{ $t('Sin enviar') }}</span>
            </template>
            <template v-else>
              <v-icon color="secondary">mdi-clock-outline</v-icon>
              <span class="ml-1 secondary--text">{{ $t('Esperando validación') }}</span>
            </template>
          </td>
          <td>{{ company.expiration_date | moment('DD/MM/YYYY') }}</td>
          <td class="text-xs-right">
            <!-- <v-btn v-if="company.status === 'validated'" small outline color="primary" :to="'/opendata/documentation/' + company.id">
                        <v-icon>mdi-eye-outline</v-icon> {{$t('Ver')}}
                    </v-btn> -->
            <template v-if="company.status === 'draft'">
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <v-btn
                    icon
                    color="primary"
                    v-on="on"
                    flat
                    :to="'/opendata/form/' + company.id + '/1'"
                  >
                    <v-icon>mdi-square-edit-outline</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t('Editar/Continuar cuestionario') }}</span>
              </v-tooltip>

              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <v-btn flat icon v-on="on" color="error" @click="$emit('delete', company)">
                    <v-icon>mdi-delete</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t('Eliminar cuestionario') }}</span>
              </v-tooltip>
            </template>

            <template v-if="company.status === 'validated' && !!company.media[0]">
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <v-btn
                    v-on="on"
                    class="px-0"
                    small
                    flat
                    :color="getChecklistColor(company)"
                    :to="'/opendata/checklist/' + company.id"
                  >
                    <v-icon>{{ getChecklistIcon(company) }}</v-icon>
                    <span v-if="checklistStatus(company) === 'completed'">
                      {{ $t('Medidas de seguridad') }}
                    </span>
                    <span v-else>{{ $t('Verificar medidas') }}</span>
                  </v-btn>
                </template>
                <span>{{ $t('Completar medidas de seguridad') }}</span>
              </v-tooltip>

              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <v-chip
                    v-on="on"
                    @click="$router.push('/opendata/checklist/' + company.id)"
                    class="px-0"
                    outline
                    :color="getChecklistColor(company)"
                    small
                    :text-color="getChecklistColor(company)"
                  >
                    <v-avatar :class="getChecklistColor(company)">
                      <v-icon class="white--text">mdi-checkbox-marked-circle-outline</v-icon>
                    </v-avatar>
                    {{ checklistCompletedPercent(company)['completed'] }}/{{
                      checklistCompletedPercent(company)['total']
                    }}
                  </v-chip>
                </template>
                <span>{{ $t('Medidas comprobadas / Medidas totales recomendadas') }}</span>
              </v-tooltip>

              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <v-btn
                    v-on="on"
                    class="px-0"
                    icon
                    outline
                    small
                    color="primary"
                    @click="downloadEntity(company)"
                  >
                    <v-icon>mdi-download-outline</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t('Descargar informe LOPD') }}</span>
              </v-tooltip>
            </template>
          </td>
        </tr>
      </og-table-body>
    </og-table>
    <div class="my-3" v-if="showLeyend">
      <h4 class="my-2">
        <v-icon>mdi-help-circle-outline</v-icon>
        {{ $t('Ayuda') }}
      </h4>
      <v-icon>mdi-square-edit-outline</v-icon>
      {{ $t('Editar/Continuar cuestionario') }}
      <v-icon class="ml-2">mdi-delete</v-icon>
      {{ $t('Eliminar cuestionario') }}
      <v-icon class="ml-2">mdi-clipboard-text-play-outline</v-icon>
      {{ $t('Completar medidas de seguridad') }}
      <v-icon class="ml-2">mdi-download-outline</v-icon>
      {{ $t('Descargar informe LOPD') }}
    </div>
  </div>
</template>

<script>
import OgTable from '@/components/commons/OgTable';
import OgTableBody from '@/components/commons/OgTableBody';
import OgTableHead from '@/components/commons/OgTableHead';
import apiCtrl from '@/services/aru-services/aru-api.service.js';

export default {
  components: {
    OgTable,
    OgTableBody,
    OgTableHead,
  },
  props: {
    showLeyend: {
      type: Boolean,
      default: true,
    },
    withActionButton: {
      type: Boolean,
      default: false,
    },
    companies: {
      type: Array,
      default: () => [],
    },
  },
  methods: {
    getChecklistColor(opendataForm) {
      if (this.checklistStatus(opendataForm) === 'completed') {
        return 'primary';
      } else {
        return 'secondary';
      }
    },
    checklistCompletedPercent(opendataForm) {
      let total = 17;
      let completed = 0;
      if (!!opendataForm && opendataForm.hasOwnProperty('checklist') && !!opendataForm.checklist) {
        total = 0;
        completed = 0;
        for (const key in opendataForm.checklist) {
          if (opendataForm.checklist.hasOwnProperty(key)) {
            const value = opendataForm.checklist[key];
            total++;
            if (value) {
              completed++;
            }
          }
        }
      }
      return { total: total, completed: completed };
    },
    checklistStatus(opendataForm) {
      if (!!opendataForm && opendataForm.hasOwnProperty('checklist') && !!opendataForm.checklist) {
        for (const key in opendataForm.checklist) {
          if (opendataForm.checklist.hasOwnProperty(key)) {
            const value = opendataForm.checklist[key];
            if (!value) {
              return 'uncompleted';
            }
          }
        }
        return 'completed';
      } else {
        return 'uncompleted';
      }
    },
    getChecklistIcon(opendataForm) {
      if (this.checklistStatus(opendataForm) === 'completed') {
        return 'mdi-clipboard-check-outline';
      } else {
        return 'mdi-clipboard-text-play-outline';
      }
    },
    downloadEntity(entity) {
      console.log(entity);
      if (entity.hasOwnProperty('media') && entity.media.length > 0) {
        apiCtrl
          .get('/document/download/' + entity.media[0].id)
          .then(res => {
            apiCtrl.downloadFile(res.data);
          })
          .catch(error => {
            console.log(error);
          });
      }
    },
  },
};
</script>

<style lang="scss">
.ogtable-opendata {
  font-size: 80%;
}

.opendata-company-row {
  cursor: pointer;
}
</style>
